/**
 * The rows Home lists: an area, an open action,
 * an interview, a moment, and the Start button.
 *
 * @module components/apps/probe/home/ProbeHomeParts
 */
import type { ProbeExaminationArea } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import { ArrowDownRightIcon, ArrowUpRightIcon, CircleIcon, MinusIcon, TargetIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import {
  areaLabel,
  areaTrend,
  interviewName,
  type ActionEntry,
  type PracticeArea,
} from "@amoreg/shared/probePractice";
import type { ProbeInterviewSummary } from "../../../../state/probeHistory";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { StatusIcon } from "../../../regulation/finding/findingGlyphs";
import { setActionDone } from "../probeActionsDone";
import { areaSlug, ASSESSMENT_LABELS, formatProbeDuration } from "../probeArea";
import { AssessmentGlyph, interviewDay, OutcomeGlyph, PROBE_LIST_LINK, PROBE_LIST_ROW, QUIET } from "../ProbeParts";
import type { HomeMoment } from "./probeHome";

/** A list row: a 16px lead, the text, then right-aligned facts, as the Briefing sets its rows. */
const HOME_ROW = cn("flex w-full min-w-0 items-center gap-3", PROBE_LIST_ROW, PROBE_LIST_LINK);

/** Home narrowed to an area, or back to all of them. */
export const homeLink = (orgSegment: string, area: ProbeExaminationArea | null) =>
  ({
    to: "/$org/apps/probe/practice",
    params: { org: orgSegment },
    search: area === null ? {} : { area: areaSlug(area) },
  }) as const;

/** An area's rating as its glyph; a faint ring when never rated. */
export function AreaGlyph({ entry }: { readonly entry: PracticeArea }) {
  return entry.latest === null ? (
    <span className="inline-flex size-4 shrink-0 items-center justify-center" title="Not rehearsed">
      <CircleIcon aria-hidden className={cn("size-3.5 opacity-50", QUIET)} />
      <span className="sr-only">Not rehearsed</span>
    </span>
  ) : (
    <AssessmentGlyph assessment={entry.latest.assessment} />
  );
}

const TREND = {
  up: { icon: ArrowUpRightIcon, label: "Better than the time before" },
  down: { icon: ArrowDownRightIcon, label: "Worse than the time before" },
  same: { icon: MinusIcon, label: "Same as the time before" },
} as const;

/** The area's latest rating in words, and the way it moved. */
export function AreaRating({ entry }: { readonly entry: PracticeArea }) {
  const trend = areaTrend(entry);
  const Trend = trend === null ? null : TREND[trend];
  return (
    <span className={cn("inline-flex items-center gap-1 whitespace-nowrap text-xs", QUIET)}>
      {entry.latest === null ? "Not rehearsed" : ASSESSMENT_LABELS[entry.latest.assessment]}
      {Trend !== null ? (
        <Trend.icon aria-label={Trend.label} className="size-3.5 shrink-0" data-probe-trend={trend} />
      ) : null}
    </span>
  );
}

/** When the area was last rehearsed; empty when never. */
export const lastRehearsed = (entry: PracticeArea): string =>
  entry.latest === null ? "" : interviewDay(entry.latest.createdAt);

/** Start an interview, on the area Home is narrowed to. */
export function StartButton({
  orgSegment,
  focus,
}: {
  readonly orgSegment: string;
  readonly focus: ProbeExaminationArea | null;
}) {
  return (
    <Button
      size="sm"
      render={
        <Link
          to="/$org/apps/probe"
          params={{ org: orgSegment }}
          search={focus === null ? {} : { focus: areaSlug(focus) }}
        />
      }
      data-probe-home-start=""
    >
      <TargetIcon className="size-3.5" />
      Start
    </Button>
  );
}

/** An open action: its box, its words, and the day of the interview that left it. */
export function ActionRow({
  action,
  orgSegment,
}: {
  readonly action: ActionEntry;
  readonly orgSegment: string;
}) {
  return (
    <li data-probe-home-action="">
      <label className={cn(HOME_ROW, "cursor-pointer items-start")}>
        <span className="flex h-5 w-4 shrink-0 items-center justify-center">
          <Checkbox
            checked={false}
            onCheckedChange={(next) => setActionDone(action.interviewId, action.text, next === true)}
            data-probe-action=""
          />
        </span>
        <span className="min-w-0 flex-1 text-pretty text-foreground" data-probe-action-text="">
          {action.text}
        </span>
        <Link
          to="/$org/apps/probe/interviews/$sessionId"
          params={{ org: orgSegment, sessionId: action.interviewId }}
          title={action.title}
          className={cn(
            "w-14 shrink-0 rounded-sm text-end text-xs leading-5 tabular-nums underline-offset-2 outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring",
            QUIET,
          )}
        >
          {interviewDay(action.createdAt)}
        </Link>
      </label>
    </li>
  );
}

/** An interview: its outcome, its name, how long it ran and its day. */
export function InterviewRow({
  interview,
  orgSegment,
}: {
  readonly interview: ProbeInterviewSummary;
  readonly orgSegment: string;
}) {
  return (
    <li>
      <Link
        to="/$org/apps/probe/interviews/$sessionId"
        params={{ org: orgSegment, sessionId: interview.id }}
        className={HOME_ROW}
        data-probe-home-interview={interview.id}
      >
        <span className="flex w-4 shrink-0 justify-center">
          {interview.debrief === null ? (
            <CircleIcon aria-hidden className={cn("size-3.5 opacity-50", QUIET)} />
          ) : (
            <OutcomeGlyph outcome={interview.debrief.outcome} />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-foreground">{interviewName(interview)}</span>
        <span className={cn("w-12 shrink-0 text-end text-xs tabular-nums max-sm:hidden", QUIET)}>
          {interview.durationMs === null ? "" : formatProbeDuration(interview.durationMs)}
        </span>
        <span className={cn("w-14 shrink-0 text-end text-xs tabular-nums", QUIET)}>
          {interviewDay(interview.createdAt)}
        </span>
      </Link>
    </li>
  );
}

/** A moment an answer fell short: why, and the day of its interview, opening it. */
export function MomentRow({
  moment,
  orgSegment,
}: {
  readonly moment: HomeMoment;
  readonly orgSegment: string;
}) {
  return (
    <li>
      <Link
        to="/$org/apps/probe/interviews/$sessionId"
        params={{ org: orgSegment, sessionId: moment.interviewId }}
        className={cn(HOME_ROW, "items-start")}
        data-probe-home-moment=""
      >
        <span aria-hidden className="flex h-5 w-4 shrink-0 items-center justify-center">
          <StatusIcon status="open" />
        </span>
        <span className="min-w-0 flex-1 text-pretty text-foreground">{moment.reason}</span>
        <span className={cn("w-14 shrink-0 text-end text-xs leading-5 tabular-nums", QUIET)}>
          {interviewDay(moment.createdAt)}
        </span>
      </Link>
    </li>
  );
}
